import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import MUIDataTable from 'mui-datatables'
import React from 'react'

import {
  getCsatResponses,
  getCsatSummary,
  getCsatFilterOptions
} from 'api/csat'
import { useAccess } from 'app/hooks/useAccess'

// Ratings are stored as the exact label strings the survey submits.
const RATINGS = [
  'Very dissatisfied',
  'Dissatisfied',
  'Neutral',
  'Satisfied',
  'Extremely Satisfied'
]

const SCORES = {
  'Very dissatisfied': 1,
  Dissatisfied: 2,
  Neutral: 3,
  Satisfied: 4,
  'Extremely Satisfied': 5
}

// The table pulls one large page and sorts/searches client-side. CSAT volume is
// in the hundreds, not millions, and this keeps sorting instant across the whole
// result rather than only the visible page.
const PAGE_LIMIT = 500

const EMPTY_FILTERS = {
  campaign: 'submitted_CSAT_Response',
  centre: '',
  segment: '',
  overall: '',
  has_active_enrolment: '',
  detractors_only: '',
  has_text: '',
  created_after: '',
  created_before: '',
  search: ''
}

const scoreColor = (score) => {
  if (score == null) return '#9e9e9e'
  if (score <= 2) return '#c0392b'
  if (score < 4) return '#f39c12'
  return '#27ae60'
}

// CSAT is a percentage, not a 1-5 score, so it needs its own thresholds.
const csatColor = (pct) => {
  if (pct == null) return '#9e9e9e'
  if (pct < 70) return '#c0392b'
  if (pct < 85) return '#f39c12'
  return '#27ae60'
}

const formatPercent = (pct) => (pct == null ? '—' : `${pct}%`)

const RatingCell = ({ value }) => {
  if (!value) return <span style={{ color: '#bbb' }}>—</span>
  const score = SCORES[value]
  return (
    <Tooltip title={value}>
      <span
        style={{ color: scoreColor(score), fontWeight: score <= 2 ? 700 : 400 }}
      >
        {score}
      </span>
    </Tooltip>
  )
}

const Csat = () => {
  // `accessLoaded` matters: until /base/me answers, isSuperAdmin is false, and
  // treating that as "not allowed" would flash the denial notice at a Super
  // Admin on every load before the page appeared.
  const { loaded: accessLoaded, isSuperAdmin } = useAccess()
  const allowed = accessLoaded && isSuperAdmin

  const [filters, setFilters] = React.useState(EMPTY_FILTERS)
  const [rows, setRows] = React.useState([])
  const [summary, setSummary] = React.useState(null)
  // {centres: [{value, parents}], categories: [{value, families}]}
  // {centres: [{value, responses}], segments: ['Preschool', ...]}
  const [filterOptions, setFilterOptions] = React.useState({
    centres: [],
    segments: []
  })
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    // A failure here must not take the page down — the table still works
    // with empty dropdowns, so it is logged and swallowed.
    getCsatFilterOptions()
      .then((data) =>
        setFilterOptions({
          centres: data?.centres || [],
          segments: data?.segments || []
        })
      )
      .catch(() => {})
  }, [])

  const load = React.useCallback(async (activeFilters) => {
    setLoading(true)
    setError('')
    try {
      const [list, sum] = await Promise.all([
        getCsatResponses({ ...activeFilters, limit: PAGE_LIMIT }),
        getCsatSummary(activeFilters)
      ])
      setRows(list?.results || list || [])
      setSummary(sum || null)
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          'Could not load CSAT responses. Check that oh-api is running.'
      )
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    if (allowed) load(EMPTY_FILTERS)
  }, [allowed, load])

  if (!accessLoaded) {
    return (
      <Box p={4} display="flex" alignItems="center">
        <CircularProgress size={22} />
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginLeft: 12 }}
        >
          Checking access…
        </Typography>
      </Box>
    )
  }

  if (!allowed) {
    return (
      <Box p={4}>
        <Typography variant="h6">CSAT 2026</Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginTop: 8 }}
        >
          This page is limited to Super Admins.
        </Typography>
      </Box>
    )
  }

  const onFilterChange = (key) => (event) =>
    setFilters((prev) => ({ ...prev, [key]: event.target.value }))

  const columns = [
    {
      name: 'created',
      label: 'Submitted',
      options: {
        customBodyRender: (v) =>
          v
            ? new Date(v).toLocaleString('en-IN', {
                dateStyle: 'medium',
                timeStyle: 'short'
              })
            : '—'
      }
    },
    {
      name: 'parent_name',
      label: 'Parent',
      options: {
        customBodyRender: (v) =>
          v || (
            <Tooltip title="The phone number typed into the survey matched no parent record">
              <span style={{ color: '#c0392b' }}>Unmatched</span>
            </Tooltip>
          )
      }
    },
    { name: 'phone_number', label: 'Phone' },
    {
      name: 'centre',
      label: 'Centre',
      options: { customBodyRender: (v) => v || '—' }
    },
    {
      name: 'children',
      label: 'Children',
      options: { customBodyRender: (v) => v || '—' }
    },
    {
      name: 'categories',
      label: 'Categories',
      options: {
        customBodyRender: (v) =>
          v ? (
            <span>
              {v.split(', ').map((c) => (
                <Chip
                  key={c}
                  label={c}
                  size="small"
                  style={{ marginRight: 4 }}
                />
              ))}
            </span>
          ) : (
            '—'
          )
      }
    },
    {
      name: 'has_active_enrolment',
      label: 'Status',
      options: {
        customBodyRender: (v) => (
          <Chip
            size="small"
            label={v ? 'Active' : 'Inactive'}
            style={{
              backgroundColor: v ? '#e8f5e9' : '#fbe9e7',
              color: v ? '#1b5e20' : '#bf360c'
            }}
          />
        )
      }
    },
    {
      name: 'teacher',
      label: 'Teaching',
      options: { customBodyRender: (v) => <RatingCell value={v} /> }
    },
    {
      name: 'admin',
      label: 'Non-teaching',
      options: { customBodyRender: (v) => <RatingCell value={v} /> }
    },
    {
      name: 'progress',
      label: 'Development',
      options: { customBodyRender: (v) => <RatingCell value={v} /> }
    },
    {
      name: 'infrastructure',
      label: 'Safety',
      options: { customBodyRender: (v) => <RatingCell value={v} /> }
    },
    {
      name: 'app',
      label: 'App',
      options: { customBodyRender: (v) => <RatingCell value={v} /> }
    },
    {
      name: 'overall',
      label: 'Overall',
      options: { customBodyRender: (v) => <RatingCell value={v} /> }
    },
    {
      name: 'average_score',
      label: 'Avg',
      options: {
        customBodyRender: (v) => (
          <strong style={{ color: scoreColor(v) }}>
            {v == null ? '—' : v}
          </strong>
        )
      }
    },
    {
      name: 'strength',
      label: 'Doing well',
      options: {
        customBodyRender: (v) =>
          v ? <span style={{ whiteSpace: 'pre-wrap' }}>{v}</span> : '—'
      }
    },
    {
      name: 'improve',
      label: 'Do better',
      options: {
        customBodyRender: (v) =>
          v ? <span style={{ whiteSpace: 'pre-wrap' }}>{v}</span> : '—'
      }
    }
  ]

  const options = {
    filter: false,
    print: false,
    download: true,
    downloadOptions: { filename: 'csat_responses.csv' },
    selectableRows: 'none',
    responsive: 'standard',
    rowsPerPage: 25,
    rowsPerPageOptions: [25, 50, 100],
    // Highlight anyone who rated any dimension Dissatisfied or worse — an
    // average of 4.2 hides the parent who rated teaching 1, and that parent is
    // the one worth calling.
    setRowProps: (_row, dataIndex) =>
      rows[dataIndex]?.is_detractor
        ? { style: { backgroundColor: '#fff5f5' } }
        : {},
    textLabels: {
      body: { noMatch: loading ? 'Loading…' : 'No responses for these filters' }
    }
  }

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        CSAT 2026
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Parent satisfaction responses from the in-app survey. Ratings show as
        1–5 (1 = Very dissatisfied). A <b>detractor</b> answered 3, 2 or 1
        overall — neutral counts, because a parent who isn&apos;t clearly happy is
        already at risk. A <b>hidden detractor</b> answered 4 or 5 overall but
        marked one specific area down; they look happy and still need a call.
      </Typography>

      {summary && (
        <>
          {/* Row 1 — volume and the per-area averages. */}
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={6} sm={3} md={2}>
              <Paper style={{ padding: 12 }}>
                <Typography variant="caption" color="textSecondary">
                  Responses
                </Typography>
                <Typography variant="h5">{summary.total_responses}</Typography>
              </Paper>
            </Grid>
            {(summary.dimensions || []).map((d) => (
              <Grid item xs={6} sm={3} md={2} key={d.key}>
                <Paper style={{ padding: 12 }}>
                  <Typography variant="caption" color="textSecondary">
                    {d.label}
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{ color: scoreColor(d.average) }}
                  >
                    {d.average == null ? '—' : d.average}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {d.positive} pos · {d.neutral} neu · {d.negative} neg
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Row 2 — the headline verdict across everyone. */}
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={6} sm={3} md={2}>
              <Paper style={{ padding: 12 }}>
                <Typography variant="caption" color="textSecondary">
                  CSAT
                </Typography>
                <Typography
                  variant="h5"
                  style={{ color: csatColor(summary.overall?.csat_percent) }}
                >
                  {formatPercent(summary.overall?.csat_percent)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  answered 4 or 5 overall
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Paper style={{ padding: 12 }}>
                <Typography variant="caption" color="textSecondary">
                  Detractors
                </Typography>
                <Typography variant="h5" style={{ color: '#c0392b' }}>
                  {summary.overall?.detractors ?? '—'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  overall 3 or below
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Paper style={{ padding: 12 }}>
                <Typography variant="caption" color="textSecondary">
                  Hidden detractors
                </Typography>
                <Typography variant="h5" style={{ color: '#e67e22' }}>
                  {summary.overall?.hidden_detractors ?? '—'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  happy overall, one area bad
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Rows 3-6 — the same four numbers per segment. A table rather than
              sixteen more cards: these are meant to be compared down a column,
              which cards make hard. Every parent is in exactly one segment, so
              the Responses column sums to the total above. */}
          <Paper style={{ marginTop: 12, padding: 12, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  {[
                    'Segment',
                    'Responses',
                    'CSAT',
                    'Detractors',
                    'Hidden detractors'
                  ].map((h, i) => (
                    <th
                      key={h}
                      style={{
                        padding: '6px 12px',
                        borderBottom: '1px solid #eee',
                        textAlign: i === 0 ? 'left' : 'right',
                        fontWeight: 500,
                        fontSize: 12,
                        color: '#777'
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(summary.segments || []).map((seg) => (
                  <tr key={seg.segment}>
                    <td style={{ padding: '6px 12px' }}>{seg.segment}</td>
                    <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                      {seg.responses}
                    </td>
                    <td
                      style={{
                        padding: '6px 12px',
                        textAlign: 'right',
                        color: csatColor(seg.csat_percent)
                      }}
                    >
                      {formatPercent(seg.csat_percent)}
                    </td>
                    <td
                      style={{
                        padding: '6px 12px',
                        textAlign: 'right',
                        color: seg.detractors ? '#c0392b' : undefined
                      }}
                    >
                      {seg.detractors}
                    </td>
                    <td
                      style={{
                        padding: '6px 12px',
                        textAlign: 'right',
                        color: seg.hidden_detractors ? '#e67e22' : undefined
                      }}
                    >
                      {seg.hidden_detractors}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Paper>
        </>
      )}

      {summary?.unmatched_phone > 0 && (
        <Typography variant="caption" style={{ color: '#bf360c' }}>
          {summary.unmatched_phone} response(s) have a phone number that matches
          no parent — shown as “Unmatched”.
        </Typography>
      )}

      <Paper style={{ padding: 16, marginTop: 12, marginBottom: 16 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              fullWidth
              label="Search name or phone"
              value={filters.search}
              onChange={onFilterChange('search')}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth>
              <InputLabel>Centre</InputLabel>
              <Select
                value={filters.centre}
                onChange={onFilterChange('centre')}
              >
                <MenuItem value="">All centres</MenuItem>
                {filterOptions.centres.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.segment}
                onChange={onFilterChange('segment')}
              >
                <MenuItem value="">All categories</MenuItem>
                {filterOptions.segments.map((seg) => (
                  <MenuItem key={seg} value={seg}>
                    {seg}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth>
              <InputLabel>Overall rating</InputLabel>
              <Select
                value={filters.overall}
                onChange={onFilterChange('overall')}
              >
                <MenuItem value="">Any</MenuItem>
                {RATINGS.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth>
              <InputLabel>Enrolment status</InputLabel>
              <Select
                value={filters.has_active_enrolment}
                onChange={onFilterChange('has_active_enrolment')}
              >
                <MenuItem value="">Any</MenuItem>
                <MenuItem value="true">Active only</MenuItem>
                <MenuItem value="false">Inactive only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth>
              <InputLabel>Show</InputLabel>
              <Select
                value={filters.detractors_only}
                onChange={onFilterChange('detractors_only')}
              >
                <MenuItem value="">All responses</MenuItem>
                <MenuItem value="true">Detractors + hidden</MenuItem>
                <MenuItem value="detractor">Detractors only</MenuItem>
                <MenuItem value="hidden">Hidden detractors only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth>
              <InputLabel>Free text</InputLabel>
              <Select
                value={filters.has_text}
                onChange={onFilterChange('has_text')}
              >
                <MenuItem value="">Any</MenuItem>
                <MenuItem value="true">Has comments</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              fullWidth
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={filters.created_after}
              onChange={onFilterChange('created_after')}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              fullWidth
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={filters.created_before}
              onChange={onFilterChange('created_before')}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth>
              <InputLabel>Campaign</InputLabel>
              <Select
                value={filters.campaign}
                onChange={onFilterChange('campaign')}
              >
                <MenuItem value="submitted_CSAT_Response">Live</MenuItem>
                <MenuItem value="submitted_CSAT_Response_TEST">Test</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => load(filters)}
              disabled={loading}
              fullWidth
            >
              {loading ? <CircularProgress size={22} /> : 'Apply'}
            </Button>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Button
              variant="outlined"
              onClick={() => {
                setFilters(EMPTY_FILTERS)
                load(EMPTY_FILTERS)
              }}
              disabled={loading}
              fullWidth
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Typography color="error" style={{ marginBottom: 12 }}>
          {error}
        </Typography>
      )}

      <MUIDataTable
        title={`${rows.length} response${rows.length === 1 ? '' : 's'}`}
        data={rows}
        columns={columns}
        options={options}
      />
    </Box>
  )
}

export default Csat
